import { useState, useEffect } from 'react';

import type { ThemeColorModes } from '@atlaskit/tokens/theme-color-modes';
import type { ThemeState } from '@atlaskit/tokens/theme-config';
import { themeStringToObject } from '@atlaskit/tokens/theme-string-to-object';

import { fg } from '@confluence/feature-gating';

import { ErrorSubjects, ErrorSubjectIds, reportThemingError } from './errorHandling.ts';
import GlobalThemePreferenceQuery from './graphql/__generated__/GlobalThemePreferenceQuery.graphql.ts';
import type {
	GlobalThemePreferenceQuery as GlobalThemePreferenceQueryType,
	GlobalThemePreferenceQueryVariables,
} from './graphql/__types__/GlobalThemePreferenceQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

const DEFAULT_COLOR_MODE: ThemeColorModes = 'auto';

/**
 * Fetches the global (account-level) theme preference from AGG.
 * This is a non-hook function that can be used outside of React components or passed to useColorModeState from @atlassian/shared-user-preferences-api.
 *
 * @param accountId - The user's account ID
 * @returns Promise that resolves to a partial ThemeState with the user's global color mode preference, or undefined if not available
 */
export const fetchGlobalColorMode = async (
	accountId: string | null | undefined,
): Promise<Partial<ThemeState> | undefined> => {
	// Return undefined if no account ID
	if (!accountId) {
		return undefined;
	}

	try {
		const { data } = await queryInterop<
			GlobalThemePreferenceQueryType,
			GlobalThemePreferenceQueryVariables
		>({
			query: GlobalThemePreferenceQuery,
			interopApolloAggClient: true,
			variables: { accountId },
			fetchPolicy: 'cache-first',
		});

		const themeString = data?.settings_userPreferencesGlobal?.theme;
		if (themeString) {
			const themeObject = themeStringToObject(themeString);
			return { colorMode: themeObject.colorMode || DEFAULT_COLOR_MODE };
		} else {
			// Data loaded but no theme string
			return { colorMode: DEFAULT_COLOR_MODE };
		}
	} catch (error) {
		// Report query errors to analytics
		void reportThemingError(ErrorSubjects.useGlobalColorMode, ErrorSubjectIds.apolloQueryErrors, {
			accountId,
			error: error.message,
			errorName: error.name,
		});
		return { colorMode: DEFAULT_COLOR_MODE };
	}
};

/**
 * Hook to fetch the global (account-level) color mode from AGG on-demand.
 * This hook queries the shared user preferences API when the feature flag is enabled.
 * It only executes on the client side and returns undefined during SSR or when disabled.
 *
 * @param accountId - The user's account ID
 * @returns The user's global color mode preference, or undefined if not available/disabled
 */
export const useGlobalColorMode = (accountId: string | null): ThemeColorModes | undefined => {
	const [colorMode, setColorMode] = useState<ThemeColorModes | undefined>(undefined);

	useEffect(() => {
		// Skip if feature flag is disabled or no account ID
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (!fg('shared-user-preferences') || !accountId) {
			return;
		}

		// Use the extracted fetch function
		fetchGlobalColorMode(accountId)
			.then((theme) => {
				if (theme?.colorMode) {
					setColorMode(theme.colorMode);
				} else {
					setColorMode(DEFAULT_COLOR_MODE);
				}
			})
			.catch((error) => {
				// This shouldn't happen as fetchGlobalColorMode handles errors internally,
				// but handle it just in case
				void reportThemingError(
					ErrorSubjects.useGlobalColorMode,
					ErrorSubjectIds.apolloQueryErrors,
					{
						accountId,
						error: error.message,
						errorName: error.name,
					},
				);
				setColorMode(DEFAULT_COLOR_MODE);
			});
	}, [accountId]);

	// Return undefined if feature flag is disabled
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	return fg('shared-user-preferences') ? colorMode : undefined;
};
